import { useCountryCodeToLicensePlate, useUser, useWorkTask } from "@tm/context-distribution"
import { VinFieldProps } from "@tm/controls"
import { useLocalization } from "@tm/localization"
import { RegistrationNoType, SystemType } from "@tm/models"
import Morpheus, { useActions } from "@tm/morpheus"
import { getSystemTypeFromUserContext } from "@tm/utils"
import { useSelector } from "react-redux"
import { useCallback, useEffect, useMemo } from "react"
import { createSelector } from "reselect"
import { useGetVins } from "../../../../../../data/VinPicker/useGetVins"
import { managerSelector } from "../../../selectors"
import { getBundleParams } from "../../../utils"
import { MainActions } from "../../main/business"
import GeneralVehicleForm from "../../_shared/GeneralVehicleFields"
import { MainState } from "../../main"
import { getDatVehicleSchema } from "../business/helpers/getDatSchemas"

export const vehicleFieldsSelector = createSelector(
    (s: MainState) => s.summary,
    (summary) => {
        const { fields, showVehicleModelDetailsLoading, datVinSearchIsValid, vinSearchInformalMessage, checkVinLoading, isGermanOrAustrianVehicle } =
            summary

        const { registrationNo, initialRegistration, minDate, maxDate, vin, plateNumber, mileage } = fields

        return {
            fields,
            showVehicleModelDetailsLoading,
            datVinSearchIsValid,
            vinSearchInformalMessage,
            checkVinLoading,
            isGermanOrAustrianVehicle,
            vehicleFields: { registrationNo, initialRegistration, minDate, maxDate, vin, plateNumber, mileage },
        }
    }
)

export default function VehicleInfoFields() {
    const user = useUser()
    const workTask = useWorkTask()
    const { translateText } = useLocalization()
    const { vehicle } = useSelector(managerSelector)
    const selector = useSelector(vehicleFieldsSelector)
    const { fields, showVehicleModelDetailsLoading, datVinSearchIsValid, vinSearchInformalMessage, checkVinLoading, isGermanOrAustrianVehicle } =
        selector
    const actions = useActions(MainActions, "checkVehicleDatInfo", "updateFieldValue", "validateVin", "initVehicle", "setValidateFields")
    const { enableDATVinSearch, enabledVinSearchHOTFIX } = getBundleParams() // TODO check if enabledVinSearchHOTFIX is still needed
    const { datPricePerRequest } = Morpheus.getParams("vehicle")
    const vehicleVinSuggestions: string[] = useGetVins("", vehicle!.tecDocTypeId) ?? []

    const schema = useMemo(
        () => getDatVehicleSchema(translateText, selector?.isGermanOrAustrianVehicle),
        [translateText, selector?.isGermanOrAustrianVehicle]
    )

    const { countryCode } = useCountryCodeToLicensePlate(vehicle?.countryCode)

    useEffect(() => {
        actions?.initVehicle && actions.initVehicle(countryCode)
    }, [vehicle])

    useEffect(() => {
        if (
            !showVehicleModelDetailsLoading &&
            enabledVinSearchHOTFIX &&
            enableDATVinSearch &&
            getSystemTypeFromUserContext(user?.userContext) === SystemType.Next &&
            vehicle?.registrationTypeId === RegistrationNoType.DatVin &&
            !datVinSearchIsValid
        ) {
            actions.checkVehicleDatInfo(workTask?.workTask?.vehicle || vehicle, translateText)
        }
    }, [vehicle, showVehicleModelDetailsLoading, datVinSearchIsValid, fields])

    const handleGetVinSuggestions = useCallback(
        (query: string): Array<string> => {
            return vehicleVinSuggestions?.length && query?.length
                ? vehicleVinSuggestions.filter((vin) => vin.startsWith(query))
                : vehicleVinSuggestions
        },
        [vehicleVinSuggestions]
    )

    const onHandleUpdateValue = useCallback(
        (path: string, value: string | Date | number) => {
            actions?.updateFieldValue(path, value)
        },
        [actions]
    )

    const handleFormValid = useCallback(
        (isValid: boolean) => {
            actions.setValidateFields("vehicleValid", isValid)
        },
        [actions]
    )

    const vinFieldProps: VinFieldProps = useMemo(() => {
        const base = {
            getSuggestionData: handleGetVinSuggestions,
        }

        const shouldAttachDATProps =
            getSystemTypeFromUserContext(user?.userContext) === SystemType.Next && enabledVinSearchHOTFIX && enableDATVinSearch

        return shouldAttachDATProps
            ? {
                  ...base,
                  datVinSearchIsValid,
                  vinSearchOptional: true,
                  datPricePerRequest,
                  vinSearchInformalMessage,
                  checkVinLoading,
                  translateText,
                  validateVin: () => actions.validateVin(workTask?.attachToWorkTask, translateText),
              }
            : base
    }, [
        handleGetVinSuggestions,
        user?.userContext,
        enabledVinSearchHOTFIX,
        enableDATVinSearch,
        datVinSearchIsValid,
        datPricePerRequest,
        vinSearchInformalMessage,
        checkVinLoading,
        translateText,
        actions,
        workTask?.attachToWorkTask,
    ])

    if (!vehicle) {
        return null
    }

    return (
        <GeneralVehicleForm
            fields={fields}
            onFormValid={handleFormValid}
            onUpdateFieldValue={onHandleUpdateValue}
            vehicle={vehicle}
            vinFieldProps={vinFieldProps}
            schema={schema}
            isGermanOrAustrianVehicle={isGermanOrAustrianVehicle}
            showVehicleModelDetailsLoading={showVehicleModelDetailsLoading}
        />
    )
}
